import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { getPendingActions, removePendingAction, getPendingCount, enqueueAction } from '../offlineQueue';

export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const refreshCount = useCallback(async () => {
    try {
      const count = await getPendingCount();
      setPendingCount(count);
    } catch {
      setPendingCount(0);
    }
  }, []);

  useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  const flush = useCallback(async () => {
    if (syncing || !navigator.onLine) return;
    setSyncing(true);
    try {
      const actions = await getPendingActions();
      for (const action of actions) {
        try {
          if (action.method === 'insert') {
            const { error } = await supabase.from(action.table).insert(action.payload);
            if (error) throw error;
          } else if (action.method === 'update') {
            const { id: rowId, ...rest } = action.payload as Record<string, unknown> & { id: string };
            const { error } = await supabase.from(action.table).update(rest).eq('id', rowId);
            if (error) throw error;
          } else if (action.method === 'rpc') {
            const { fn_name, ...params } = action.payload as Record<string, unknown> & { fn_name: string };
            const { error } = await supabase.rpc(fn_name, params);
            if (error) throw error;
          }
          if (action.id != null) await removePendingAction(action.id);
        } catch {
          break;
        }
      }
    } finally {
      setSyncing(false);
      await refreshCount();
    }
  }, [syncing, refreshCount]);

  useEffect(() => {
    if (isOnline && pendingCount > 0) {
      flush();
    }
  }, [isOnline, pendingCount, flush]);

  const queueAction = useCallback(async (
    table: string,
    method: 'insert' | 'update' | 'rpc',
    payload: Record<string, unknown>,
  ) => {
    await enqueueAction({ table, method, payload });
    await refreshCount();
  }, [refreshCount]);

  return { isOnline, pendingCount, syncing, flush, queueAction };
}
